import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Store, MealPlan, Meal, SavedTrip, Ingredient } from '@/lib/index';

export interface FlyerItem { name: string; price: number; cat: string; qty?: string; }
export interface MyPickItem { id: string; name: string; qty?: string; }

// A saved recipe is just a full Meal + when it was saved
export interface SavedRecipe extends Meal {
  savedAt: string;   // ISO date string
  sourcePlan?: string; // e.g. store name
}

type AppStep = 'home' | 'stores' | 'scanning' | 'mealplan';

interface AppState {
  step:           AppStep;
  postalCode:     string;
  stores:         Store[];
  selectedStore:  Store | null;
  mealPlan:       MealPlan | null;
  cartMeals:      Meal[];
  savedTrips:     SavedTrip[];
  savedRecipes:   SavedRecipe[];
  flyerItems:     FlyerItem[];
  myPickItems:     MyPickItem[];
  error:          string | null;
  setStep:        (s: AppStep) => void;
  setPostalCode:  (p: string) => void;
  setStores:      (stores: Store[]) => void;
  selectStore:    (store: Store) => void;
  setMealPlan:    (plan: MealPlan) => void;
  addToCart:      (meal: Meal) => void;
  removeFromCart: (id: string) => void;
  clearCart:      () => void;
  /** Toggle a pantry ingredient in/out of a cart meal's ingredient list */
  togglePantryIngredient: (mealId: string, ingredient: Ingredient) => void;
  saveTrip:       (trip: SavedTrip) => void;
  deleteTrip:     (id: string) => void;
  setTrips:        (trips: SavedTrip[]) => void;
  swapCartMeals:   (i: number, j: number) => void;
  addFlyerItem:    (item: FlyerItem) => void;
  removeFlyerItem: (name: string) => void;
  clearFlyerItems: () => void;
  // My Picks (freeform grocery additions)
  addMyPick:       (name: string, qty?: string) => void;
  removeMyPick:    (id: string) => void;
  clearMyPicks:    () => void;
  // Saved Recipes
  saveRecipe:      (meal: Meal, sourcePlan?: string) => void;
  unsaveRecipe:    (id: string) => void;
  isRecipeSaved:   (id: string) => boolean;
  setSavedRecipes: (recipes: SavedRecipe[]) => void;
  updateMealPhotos: (photos: Record<string, string>) => void;
  /** Replace a single meal in the active meal plan (swap feature) */
  swapMealInPlan:  (mealId: string, newMeal: Meal) => void;
  setError:       (e: string | null) => void;
  reset:          () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      step:          'home' as AppStep,
      postalCode:    '' as string,
      stores:        [] as Store[],
      selectedStore: null as Store | null,
      mealPlan:      null as MealPlan | null,
      cartMeals:     [] as Meal[],
      savedTrips:    [] as SavedTrip[],
      savedRecipes:  [] as SavedRecipe[],
      flyerItems:    [] as FlyerItem[],
      myPickItems:   [] as MyPickItem[],
      error:         null as string | null,

      setStep:       (step)          => set({ step }),
      setPostalCode: (postalCode)    => set({ postalCode }),
      setStores:     (stores)        => set({ stores }),
      selectStore:   (selectedStore) => set({ selectedStore }),
      setMealPlan:   (mealPlan)      => set({ mealPlan }),

      addToCart: (meal) => {
        const prev = get().cartMeals;
        if (prev.some(m => m.id === meal.id)) return;
        set({ cartMeals: [...prev, meal] });
      },

      removeFromCart: (id) =>
        set({ cartMeals: get().cartMeals.filter(m => m.id !== id) }),

      clearCart: () => set({ cartMeals: [] }),

      togglePantryIngredient: (mealId, ingredient) => {
        const meals = get().cartMeals.map(m => {
          if (m.id !== mealId) return m;
          const already = m.ingredients.some(i => i.name === ingredient.name);
          const ingredients = already
            ? m.ingredients.filter(i => i.name !== ingredient.name)
            : [...m.ingredients, { ...ingredient, price: 0, on_sale: false }];
          return { ...m, ingredients };
        });
        set({ cartMeals: meals });
      },

      saveTrip:   (trip)  => set({ savedTrips: [trip, ...get().savedTrips] }),
      deleteTrip: (id)    => set({ savedTrips: get().savedTrips.filter(t => t.id !== id) }),
      setTrips:   (trips) => set({ savedTrips: trips }),
      swapCartMeals: (i, j) => {
        const meals = [...get().cartMeals];
        if (i < 0 || j < 0 || i >= meals.length || j >= meals.length) return;
        [meals[i], meals[j]] = [meals[j], meals[i]];
        set({ cartMeals: meals });
      },
      addFlyerItem: (item) => {
        if (get().flyerItems.some(f => f.name === item.name)) return;
        set({ flyerItems: [...get().flyerItems, item] });
      },
      removeFlyerItem: (name) => set({ flyerItems: get().flyerItems.filter(f => f.name !== name) }),
      clearFlyerItems: () => set({ flyerItems: [] }),

      // ── My Picks ──────────────────────────────────────────────────────────
      addMyPick: (name, qty) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        set({ myPickItems: [...get().myPickItems, { id: crypto.randomUUID(), name: trimmed, qty }] });
      },
      removeMyPick: (id) => set({ myPickItems: get().myPickItems.filter(p => p.id !== id) }),
      clearMyPicks: () => set({ myPickItems: [] }),

      // ── Saved Recipes ──────────────────────────────────────────────────────
      saveRecipe: (meal, sourcePlan) => {
        if (get().savedRecipes.some(r => r.id === meal.id)) return; // already saved
        const recipe: SavedRecipe = { ...meal, savedAt: new Date().toISOString(), sourcePlan };
        set({ savedRecipes: [recipe, ...get().savedRecipes] });
      },
      unsaveRecipe: (id) =>
        set({ savedRecipes: get().savedRecipes.filter(r => r.id !== id) }),
      isRecipeSaved: (id) => get().savedRecipes.some(r => r.id === id),
      setSavedRecipes: (recipes) => set({ savedRecipes: recipes }),

      // ── Recipe Photos (ChefGPT) ──────────────────────────────────────────────
      swapMealInPlan: (mealId, newMeal) => {
        const plan = get().mealPlan;
        if (!plan) return;
        const meals = plan.meals.map(m => m.id === mealId ? { ...newMeal, id: mealId, day: m.day, meal_type: m.meal_type } : m);
        set({ mealPlan: { ...plan, meals } });
      },
      updateMealPhotos: (photos) => {
        const plan = get().mealPlan;
        if (!plan) return;
        const updatedMeals = plan.meals.map(m =>
          photos[m.id] ? { ...m, photo_url: photos[m.id] } : m
        );
        set({ mealPlan: { ...plan, meals: updatedMeals } });
      },

      setError:   (error) => set({ error }),

      reset: () => set({
        step: 'home', postalCode: '', stores: [], selectedStore: null,
        mealPlan: null, cartMeals: [], error: null,
      }),
    }),
    {
      name: 'flyerfeast-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        cartMeals:     state.cartMeals,
        savedTrips:    state.savedTrips,
        savedRecipes:  state.savedRecipes,
        flyerItems:    state.flyerItems,
        myPickItems:   state.myPickItems,
        postalCode:    state.postalCode,
        selectedStore: state.selectedStore,
      }),
    }
  )
);
